import { useCallback, useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

type DateRange = {
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
};

export type ServicerFeeFiltersState = {
  creditFacilityIds: string[];
  accrualDates: DateRange;
  loanIds?: string[];
  capitalProviderOrganizationIds?: string[];
};

export const useServicerFeeFilters = ({
  creditFacilityIds = [],
  loanIds = [],
  capitalProviderOrganizationIds = [],
  accrualDates,
}: Omit<ServicerFeeFiltersState, 'accrualDates'> & {
  accrualDates?: DateRange;
}): {
  filters: ServicerFeeFiltersState;
  setFilters: (inputs: Partial<ServicerFeeFiltersState>) => void;
} => {
  const initialAccrualDatesState = useMemo<DateRange>(() => {
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = commonDate.dateToFullDateString(new Date(), clientTimeZone);
    const oneMonthAgo = commonDate.addMonths(now, -1);

    return { startDate: oneMonthAgo, endDate: now };
  }, []);

  // *** Accrual Dates ***
  const [selectedAccrualDates, setSelectedAccrualDates] = useState<DateRange>(
    accrualDates ?? initialAccrualDatesState,
  );

  // *** Facilities ***
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>(
    creditFacilityIds,
  );

  // *** Lenders ***
  const [selectedCapitalProviderIds, setSelectedCapitalProviderIds] = useState<
    string[]
  >(capitalProviderOrganizationIds);

  // *** Loan Statuses ***
  const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>(loanIds);

  const setFilters = useCallback(
    (nextFilters: Partial<ServicerFeeFiltersState>): void => {
      if (nextFilters.accrualDates) {
        setSelectedAccrualDates({
          startDate: nextFilters.accrualDates.startDate,
          endDate: nextFilters.accrualDates.endDate,
        });
      }
      if (nextFilters.loanIds) {
        setSelectedLoanIds(nextFilters.loanIds);
      }
      if (nextFilters.creditFacilityIds) {
        setSelectedFacilityIds(nextFilters.creditFacilityIds);
      }
      if (nextFilters.capitalProviderOrganizationIds) {
        setSelectedCapitalProviderIds(
          nextFilters.capitalProviderOrganizationIds,
        );
      }
    },
    [],
  );

  return {
    filters: {
      creditFacilityIds: selectedFacilityIds,
      loanIds: selectedLoanIds,
      capitalProviderOrganizationIds: selectedCapitalProviderIds,
      accrualDates: selectedAccrualDates,
    },
    setFilters,
  };
};
